import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

const AddDegreeProgram = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    degreeProgrammeName: '',
    degreeLevel: '',
    localInternational: '',
    duration: 4,
    description: '',
    status: 'Active'
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.degreeProgrammeName) {
      alert('Degree Programme Name is required');
      return;
    }

    if (!formData.degreeLevel) {
      alert('Degree Level is required');
      return;
    }

    if (!formData.localInternational) {
      alert('Local/International selection is required');
      return;
    }

    if (!currentUser) {
      alert('Please log in to add degree programs');
      return;
    }

    try {
      setLoading(true);

      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (!adminError && adminData) {
          universityId = adminData.university_id;
        }
      }

      if (!universityId) {
        alert('Unable to determine your university. Please contact support.');
        return;
      }

      console.log('Adding degree program for university:', universityId);

      // Prepare degree program data for Supabase
      const degreeData = {
        degree_programme: formData.degreeProgrammeName,    // Degree Programme Name -> degree_programme
        degree_level: formData.degreeLevel.toLowerCase(),   // Degree Level -> degree_level (lowercase)
        local_international: formData.localInternational.toLowerCase(), // Local/International -> local_international (lowercase)
        duration_years: parseInt(formData.duration),       // Duration -> duration_years (integer)
        description: formData.description || null,         // Description -> description
        active: formData.status === 'Active',              // Status -> active (boolean)
        university_id: universityId,                       // Admin's university_id
        created_at: new Date().toISOString()
      };

      console.log('Degree program data being submitted to Supabase:', degreeData);

      // Insert degree program into Supabase
      const { data: degreeResult, error: degreeError } = await supabase
        .from('degree_details')
        .insert([degreeData])
        .select()
        .single();

      if (degreeError) {
        console.error('Error inserting degree program:', degreeError);
        console.error('Full error details:', JSON.stringify(degreeError, null, 2));
        throw degreeError;
      }

      console.log('Degree program created successfully:', degreeResult);
      alert('Degree Program added successfully!');
      navigate('/academic-management');

    } catch (error) {
      console.error('Error adding degree program:', error);
      let errorMessage = 'Error adding degree program: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to add degree programs.
            </p>
          </div>
        ) : (
        <>
        {/* Page Title */}
        <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
          Add Degree Program
        </h1>

        {/* Add Degree Program Content Area */}
        <form onSubmit={handleSubmit}>
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center',
                fontSize: 16,
                fontFamily: 'Outfit',
                color: '#F04915'
              }}>
                Adding degree program...
              </div>
            </div>
          )}
          <div style={{marginTop: '20px', margin: '20px 40px'}}>
            <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
              <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
                Degree Program Details
              </h2>
            </div>

            <div style={{
              background: '#FFFBF5', 
              padding: '40px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15
            }}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Programme Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="degreeProgrammeName"
                      type="text"
                      placeholder="Software Engineering"
                      value={formData.degreeProgrammeName}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Level <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="degreeLevel"
                      value={formData.degreeLevel}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Degree Level</option>
                      <option value="undergraduate">Undergraduate</option>
                      <option value="postgraduate">Postgraduate</option>
                      <option value="doctoral">Doctoral</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Duration (Years) <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="duration"
                      type="number"
                      min="1"
                      max="8"
                      value={formData.duration}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Local/International <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="localInternational"
                      value={formData.localInternational}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Type</option>
                      <option value="local">Local</option>
                      <option value="international">International</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    placeholder="Enter degree program description..."
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{display: 'flex', justifyContent: 'space-between', margin: '40px'}}>
            <Link to="/academic-management">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Academic Management
              </button>
            </Link>
            <button 
              type="submit" 
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F04915';
              }}
            >
              Submit
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default AddDegreeProgram;
